import Cookies from 'js-cookie'

import type { Business } from 'types/public-playlist'

import { __REACT_NATIVE__ } from '@disco/constants/environment'

/**
 * Gets a cookie value by name and sanitizes it by removing special encoding.
 *
 * @param name - The name of the cookie to retrieve
 * @returns The sanitized cookie value or null if not found or in a React Native environment
 */
export function getCookie(name: string): string | null {
  if (__REACT_NATIVE__ || typeof document === 'undefined') return null
  const value = Cookies.get(name)
  if (value) {
    return value
      .replace(/(^"|"$)/g, '')
      .replace(/\\054/g, ',')
      .replace(/\\"/g, '"')
      .replace(/\\073/g, ';')
  }
  return null
}

/** Same-origin endpoint returning the user's visible businesses (SLAM-2213). */
export const VISIBLE_BUSINESSES_PATH = '/api-auth/businesses/'

export function normalizeBusinesses(businesses: unknown): Array<Business> {
  if (!Array.isArray(businesses)) return []
  return businesses
    .map(obj => ({
      id: obj?.id,
      name: obj?.name,
      url: obj?.url,
    }))
    .sort((a, b) => {
      const nameA = a.name.toUpperCase()
      const nameB = b.name.toUpperCase()
      if (nameA < nameB) return -1
      if (nameA > nameB) return 1
      return 0
    })
}

export function normalizeBusinessTuples(tuples: unknown): Array<Business> {
  if (!Array.isArray(tuples)) return []
  return tuples
    .map(tuple => ({
      id: tuple?.[0],
      name: tuple?.[1],
      url: tuple?.[2],
    }))
    .sort((a, b) => {
      const nameA = a.name.toUpperCase()
      const nameB = b.name.toUpperCase()
      if (nameA < nameB) return -1
      if (nameA > nameB) return 1
      return 0
    })
}

/**
 * Retrieves the list of businesses the user has access to from cookies.
 *
 * Parses the disco_businesses cookie and transforms it into an array of Business objects.
 * Returns an empty array if the cookie doesn't exist or can't be parsed.
 * The businesses are sorted alphabetically by name.
 *
 * @returns Array of Business objects the user has access to
 */
export default function businessCookie(): Array<Business> {
  const businessCookie =
    typeof document !== 'undefined' ? getCookie('disco_businesses') : null
  try {
    if (businessCookie === null) return []
    return normalizeBusinessTuples(JSON.parse(businessCookie))
  } catch {
    return []
  }
}

/**
 * Checks if the user is part of any business.
 *
 * @returns True if the user belongs to at least one business, false otherwise
 */
export function isUserInBusiness(): boolean {
  return businessCookie().length > 0
}
